import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Users, Target, Lightbulb, Award, Rocket, Heart, Eye } from "lucide-react";

const About = () => {
  const values = [
    {
      icon: Lightbulb,
      title: "Innovation First",
      description: "We push boundaries and embrace cutting-edge technology to deliver solutions that matter.",
      color: "from-amber-500 to-orange-600",
    },
    {
      icon: Users,
      title: "Community Driven",
      description: "Building together with our community, fostering collaboration and shared growth.",
      color: "from-blue-500 to-cyan-600",
    },
    {
      icon: Target,
      title: "Results Focused",
      description: "Every solution we create is designed to deliver measurable impact and real value.",
      color: "from-emerald-500 to-teal-600",
    },
    {
      icon: Heart,
      title: "Passion for Tech",
      description: "We live and breathe technology, turning complex challenges into elegant solutions.",
      color: "from-pink-500 to-rose-600",
    },
  ];

  const team = [
    { name: "Alex Chen", role: "CEO & Founder", image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face" },
    { name: "Sarah Mitchell", role: "CTO", image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300&h=300&fit=crop&crop=face" },
    { name: "Marcus Johnson", role: "Head of Design", image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face" },
    { name: "Emily Rodriguez", role: "Lead Developer", image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop&crop=face" },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      
      <main className="pt-16">
        {/* Hero Section */}
        <section className="py-24 relative overflow-hidden px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="absolute inset-0">
            <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto text-center relative z-10">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-sm font-medium mb-6 animate-fade-in">
              About Us
            </span>
            <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6 animate-fade-in">
              Driving the Future of
              <span className="gradient-text block">Technology</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto animate-fade-in">
              The Tech Bus was founded with a simple mission: to make cutting-edge technology 
              accessible to businesses of all sizes and accelerate digital transformation.
            </p>
          </div>
        </section>

        {/* Mission & Vision Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="container mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-start mb-20">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                    <Rocket className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-3xl font-heading font-bold">Our Mission</h2>
                </div>
                <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                  We believe that technology should empower, not complicate. Our mission is to 
                  bridge the gap between complex technological innovations and practical business 
                  applications, helping organizations thrive in the digital age.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Since our founding, we've helped hundreds of businesses transform their 
                  operations, reach new customers, and achieve growth through 
                  strategic technology implementation.
                </p>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                    <Eye className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-3xl font-heading font-bold">Our Vision</h2>
                </div>
                <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                  To be the most trusted technology partner for businesses worldwide—enabling 
                  every organization to build, scale, and secure exceptional digital experiences 
                  without compromise.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  We envision a world where cutting-edge development and design services are 
                  accessible, transparent, and aligned with our clients' long-term success.
                </p>
              </div>
            </div>
            <div className="relative rounded-3xl glass-card p-8 md:p-12 text-center max-w-2xl mx-auto">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-4">
                <Award className="w-8 h-8 text-white" />
              </div>
              <p className="text-4xl font-heading font-bold gradient-text mb-2">500+</p>
              <p className="text-muted-foreground text-lg">Businesses Transformed</p>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                Our Values
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Our Core Values</h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                These principles guide everything we do and shape how we work with our clients.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
              {values.map((value, index) => (
                <div
                  key={value.title}
                  className="p-6 rounded-2xl glass-card hover-lift cursor-pointer group"
                >
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${value.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <value.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold mb-2">{value.title}</h3>
                  <p className="text-muted-foreground">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                Our Team
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Meet Our Team</h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                The passionate individuals behind The Tech Bus, dedicated to your success.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 stagger-children">
              {team.map((member, index) => (
                <div
                  key={member.name}
                  className="text-center group"
                >
                  <div className="relative mb-4 inline-block">
                    <div className="w-36 h-36 mx-auto rounded-2xl overflow-hidden ring-4 ring-border group-hover:ring-primary/50 transition-all duration-300">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                  </div>
                  <h3 className="text-lg font-heading font-semibold">{member.name}</h3>
                  <p className="text-muted-foreground text-sm">{member.role}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About;
